import React, { FC, useEffect, useState } from "react"

import { FilterOutlined, LoadingOutlined } from "@ant-design/icons"
import { Button, Col, ConfigProvider, DatePicker, Form, Input, InputNumber, Row, Select, Spin, Tag } from "antd"
import locale from "antd/locale/ru_RU"
import dayjs from "dayjs"
import "dayjs/locale/ru"
import { useDispatch } from "react-redux"

import { IAdsFilterLists } from "../../../../types/ads"
import { adsApi } from "../../../api/endpoints/adsApi"
import { handleResponseError } from "../../../api/middleware"
import { adminRole } from "../../../constants/roles"
import { useActions } from "../../../hooks/useActions"
import { useAllowedRoles } from "../../../hooks/useAllowedRoles"

dayjs.locale("ru")

// Цвета для статусов
const statusColors: Record<string, string> = {
    "Наша квартира": "#22c55e",
    "Звонок": "#3b82f6",
    "Новое": "#6b7280",
    "Не снял": "#ef4444",
    "Не дозвонился": "#f59e0b",
}

// Цвета для источников
const sourceColors: Record<string, string> = {
    "Авито": "#22c55e",
    "ЦИАН": "#1677ff",
    "ЮЛА": "#7092fe",
}

// Цвета для групп
const groupColors: Record<string, string> = {
    "Аренда. Жилая": "#8b5cf6",
    "Аренда. Коммерческая": "#ec4899",
}

interface IProps {
    isLoading: boolean
}

const FilterTable: FC<IProps> = ({ isLoading }) => {
    const dispatch = useDispatch()
    const [ form ] = Form.useForm()
    const isAdmin = useAllowedRoles([ adminRole ])

    const [ isLoadingLists, setIsLoadingLists ] = useState(false)
    const [ filterLists, setFilterLists ] = useState<IAdsFilterLists>({
        userList: [],
        statusList: [],
        metroList: [],
        sourceList: [],
        groupList: [],
        roomList: []
    })

    const { setAdsTableFilters, setAdsFetching } = useActions()

    const fetchListsData = () => {
        setIsLoadingLists(true)

        adsApi.fetchLists()
            .then((response) => {
                if (response.status === 200) {
                    const listData = response.data

                    setFilterLists({
                        userList: listData.operator_list,
                        statusList: listData.call_status_list,
                        metroList: listData.metro_list,
                        sourceList: listData.source_list,
                        groupList: listData.group_list,
                        roomList: listData.list_of_rooms
                    })
                }
            })
            .catch((err) => {
                handleResponseError(dispatch, err)
            })
            .finally(() => {
                setIsLoadingLists(false)
            })
    }

    useEffect(() => {
        fetchListsData()
    }, [])

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSubmitFilter = (values: any) => {
        const start = values.data ? values.data[0] : null
        const end = values.data ? values.data[1] : null

        const data = {
            ...values,
            data: { start: start, end: end }
        }

        setAdsTableFilters(data)
        setAdsFetching(true)
    }

    return (
        <ConfigProvider locale={locale}>
            <Spin spinning={isLoadingLists || isLoading} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
                <div className="filter-form">
                    <Form
                        form={form}
                        onFinish={onSubmitFilter}
                        name="filter"
                        layout="vertical"
                    >
                        <Row gutter={12}>
                            <Col span={6}>
                                <Form.Item name="data">
                                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                                    {/* @ts-ignore */}
                                    <DatePicker.RangePicker
                                        format="DD.MM.YYYY"
                                        style={{ width: "100%" }}
                                        placeholder={[ "От", "До" ]}
                                        size="middle"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item name="status_id">
                                    <Select 
                                        mode="multiple" 
                                        placeholder="Статус"
                                        allowClear
                                        maxTagCount={1}
                                    >
                                        {filterLists?.statusList?.map((item, index) => (
                                            <Select.Option key={index} value={item.id}>
                                                <Tag 
                                                    color={statusColors[item.name] || "#6b7280"} 
                                                    style={{ marginRight: 0 }}
                                                >
                                                    {item.name}
                                                </Tag>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={5}>
                                <Form.Item name="metro_id">
                                    <Select
                                        mode="multiple"
                                        placeholder="Метро"
                                        showSearch
                                        allowClear
                                        maxTagCount={1}
                                        optionFilterProp="children"
                                        filterOption={(input, option) => {
                                            const text = option?.["data-search"] as string || ""
                                            return text.toLowerCase().includes(input.toLowerCase())
                                        }}
                                        filterSort={(optionA, optionB) => {
                                            const nameA = optionA?.["data-name"] as string || ""
                                            const nameB = optionB?.["data-name"] as string || ""
                                            return nameA.toLowerCase().localeCompare(nameB.toLowerCase())
                                        }}
                                    >
                                        {filterLists?.metroList?.map((item, index) => (
                                            <Select.Option 
                                                key={index} 
                                                value={item.id}
                                                data-search={`${item.name} ${item.line_name}`}
                                                data-name={item.name}
                                            >
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <div
                                                        style={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: "50%",
                                                            backgroundColor: item.color ? `#${item.color}` : "#ccc",
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                    <span style={{ fontSize: 13 }}>{item.name}</span>
                                                    <span style={{ color: "#9ca3af", fontSize: 11 }}>
                                                        {item.line_name}
                                                    </span>
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item name="source_id">
                                    <Select 
                                        mode="multiple" 
                                        placeholder="Источник"
                                        allowClear
                                        maxTagCount={1}
                                    >
                                        {filterLists?.sourceList?.map((item, index) => (
                                            <Select.Option key={index} value={item.id}>
                                                <Tag 
                                                    color={sourceColors[item.name] || "#ef961f"} 
                                                    style={{ marginRight: 0 }}
                                                >
                                                    {item.name}
                                                </Tag>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item name="group_id">
                                    <Select 
                                        mode="multiple" 
                                        placeholder="Группа"
                                        allowClear
                                        maxTagCount={1}
                                    >
                                        {filterLists?.groupList?.map((item, index) => (
                                            <Select.Option key={index} value={item.id}>
                                                <Tag 
                                                    color={groupColors[item.name] || "#6b7280"} 
                                                    style={{ marginRight: 0 }}
                                                >
                                                    {item.name}
                                                </Tag>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="room_id">
                                    <Select 
                                        mode="multiple" 
                                        placeholder="Комнат"
                                        allowClear
                                        maxTagCount={2}
                                    >
                                        {filterLists?.roomList?.map((item, index) => (
                                            <Select.Option key={index} value={item.id}>
                                                {item.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={12}>
                            <Col span={4}>
                                <Form.Item name="price_start">
                                    <InputNumber 
                                        placeholder="Цена от" 
                                        style={{ width: "100%" }}
                                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        parser={(value: any) => value?.replace(/\s/g, "")}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="price_end">
                                    <InputNumber 
                                        placeholder="Цена до" 
                                        style={{ width: "100%" }}
                                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        parser={(value: any) => value?.replace(/\s/g, "")}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="phone">
                                    <Input placeholder="Телефон" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="item_id">
                                    <Input placeholder="№ объявления" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="address">
                                    <Input placeholder="Адрес" allowClear />
                                </Form.Item>
                            </Col>
                            {isAdmin && (
                                <Col span={4}>
                                    <Form.Item name="user_id">
                                        <Select 
                                            mode="multiple" 
                                            placeholder="Оператор"
                                            allowClear
                                            maxTagCount={1}
                                        >
                                            {filterLists?.userList?.map((item, index) => (
                                                <Select.Option key={index} value={item.id}>
                                                    {item.name}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            )}

                            <Col span={4}>
                                <Form.Item>
                                    <Button
                                        type="primary"
                                        icon={<FilterOutlined />}
                                        onClick={() => form.submit()}
                                        style={{ marginRight: 8 }}
                                    >
                                        Найти
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            form.resetFields()
                                            setAdsTableFilters({
                                                data: { start: "", end: "" },
                                                user_id: 0,
                                                status_id: 0,
                                                metro_id: 0,
                                                phone: "",
                                                source_id: 0,
                                                group_id: 0,
                                                room_id: 0,
                                                item_id: "",
                                                address: "",
                                            })
                                            setAdsFetching(true)
                                        }}
                                    >
                                        Сброс
                                    </Button>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </Spin>
        </ConfigProvider>
    )
}

export default FilterTable
